<template>
    <div>
        <v-container :class="[{ 'pa-0': noContainer }]">
            <skeleton-loader v-if="loading" type="assigned-detail" />
            <v-row v-else>
                <v-col cols="12" md="6" :offset-md="isCompetence ? 3 : 0">
                    <v-row>
                        <v-col cols="12">
                            <v-img eager contain :class="['img-routine', { 'img-routine-mobile': isMobile }]"
                                :src="routineImg" />
                        </v-col>
                        <!-- BASIC INFO -->
                        <v-col cols="12" v-if="isCompetence">
                            <div :class="['comp-card', { 'comp-card-mobile': isMobile }]">
                                <div class="comp-title">{{ $t('common.competence') }}</div>
                                <div v-for="(item, i) in competitionInfo" :key="i" class="comp-item">
                                    <span class="comp-label">{{ item.label }}</span>
                                    <span class="comp-value">{{ item.value }}</span>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" v-else>
                            <div :class="['stat-row', { 'stat-row-mobile': isMobile }]">
                                <div class="stat-tile">
                                    <div class="stat-lbl">{{ $tc(`common.minutes`, 2) }}</div>
                                    <div class="stat-val">{{ routineDuration }}</div>
                                </div>
                                <div class="stat-tile">
                                    <div class="stat-lbl">{{ $tc(`common.blocks`, 2) }}</div>
                                    <div class="stat-val">{{ blocks.length }}</div>
                                </div>
                                <div class="stat-tile">
                                    <div class="stat-lbl">{{ $tc('exercises.exercises', 2) }}</div>
                                    <div class="stat-val">{{ exercisesQuantity }}</div>
                                </div>
                            </div>
                        </v-col>

                        <!-- EQUIPMENTS -->
                        <v-col cols="12" v-if="equipments.length > 0">
                            <div class="equipments text-center">
                                {{ $t('equipments.equip') }}
                            </div>
                            <div class="text-center">
                                <v-chip v-for="(equip, i) in equipments" :key="i" label outlined class="mr-1 mt-1">
                                    <v-img :src="equipImg(equip)" eager contain max-width="35" class="mr-3" />
                                    {{ equipmentLabel(equip) }}
                                </v-chip>
                            </div>
                        </v-col>

                        <!-- SHOW SUMMARY -->
                        <v-col cols="12" v-if="percentReady === 100">
                            <v-btn block @click="$emit('showSummary')" color="primary" class="normal-btn black--text"
                                depressed>
                                {{ $t('routines.watch_summary') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-col>

                <v-col cols="12" md="6" v-if="isPlaylistWeightMode">
                    <weight-mode-exercises :routine="currentRoutine" :exercises="weightExercises"
                        @complete="onCompleteWeightRoutine" />
                </v-col>
                <v-col cols="12" md="6" v-else-if="!isCompetence">
                    <v-expansion-panels flat accordion v-model="carousel" class="blocks-panels">
                        <v-expansion-panel v-for="(block, i) in blocks" :key="i">
                            <block-expansion-header :hide-student-info="hideStudentInfo" :block="block"
                                :loading-complete="loadingComplete" :readonly="readonly" :index="i"
                                @complete="setBlockComplete" ref="blockHeader" />
                            <v-expansion-panel-content>
                                <div v-for="(exercise, j) in block.exercises" :key="j" class="ex-wrap">
                                    <exercise-card :exercise="exercise" :show-complete-sets="showCompleteSets"
                                        :block="block" :readonly="readonly" withMenu
                                        :changes="changesFor(i, exercise)" @click:video="onWatchStudentVideo"
                                        no-redirect
                                        @click:image="onClickExerciseImage"
                                        @click:config="onClickExerciseConfig(exercise, block, i, j)"
                                        @click:menu:timer="handleTimer"
                                        @click:menu:exercise="handleExercise(exercise, i, j)"
                                        @click:menu:camera="handleCamera(exercise, i, j)" @click:menu:skip="handleSkip(exercise, i)"
                                        @click:menu:history="handleHistory(exercise)"
                                        @click:menu:pain="handlePainScale(exercise, i)" />
                                    <rest-card :rest="exercise" v-if="exercise.hasTime" readonly />
                                </div>
                                <div v-if="block.isCircuit">
                                    <v-btn v-if="!block.completed && !loadingComplete" :loading="loadingComplete"
                                        :disabled="readonly || block.completed" color="primary"
                                        class="black--text float-right mt-3 mb-2" small depressed fab
                                        @click="showTimerDialog(i)">
                                        <v-icon>mdi-timer-outline</v-icon>
                                    </v-btn>
                                    <v-btn v-if="block.tabata" class="float-right mt-3 mb-2 mr-3"
                                        style="pointer-events: none;" height="40" depressed outlined>
                                        <v-icon left>mdi-alarm-snooze</v-icon>
                                        {{ block.tabata.timeRest }}'
                                    </v-btn>
                                </div>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <mobile-spacer :height="isCompetence ? 0 : 74" />
                </v-col>

            </v-row>

            <note-message-dialog ref="note" />
            <routine-footer v-if="!hideStudentInfo && !isPlaylistWeightMode && !isCompetence" :percent="percentReady" />
            <watch-video-dialog v-if="video" :thumb="img" :video="video" ref="watchVideo" :commentary="commentary" />
            <edit-sets-dialog ref="editSets" @updatedSet="onUpdatedSet" @sentCommentary="onSentCommentary"
                :commentary="commentary" />

            <!-- MENU ACTIONS -->
            <change-exercise-dialog ref="changeExercise" :exercise="exerciseEditing"
                @success="onChangeExerciseSuccess" />
            <timer-dialog :duration="-1" infinite closable ref="timer" />
            <pain-scale-dialog ref="painScale" @success="onPainScale" />
            <record-video-dialog ref="recordVideo" :exercise="exerciseEditing"
                :assigned-routine-id="assignedRoutineId" />
            <exercise-history-dialog :routine="assignedRoutine" ref="history" />
        </v-container>
    </div>
</template>

<script>

import ExerciseCard from "@/components/common/routines/ExerciseCard";
import RoutineFooter from "@/components/common/routines/RoutineFooter";
import { mapActions, mapMutations, mapState } from "vuex";
import { db, FieldValue } from "@/utils/Firebase";

import { blockUniqueTime, getMergedBlocks } from "@/utils/Exercises";
import RestCard from "@/components/coach/routines/RestCard";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import BlockExpansionHeader from "@/components/common/routines/BlockExpansionHeader";
import NoteMessageDialog from './NoteMessageDialog.vue';
import TimerDialog from './TimerDialog.vue';
import WatchVideoDialog from './WatchVideoDialog.vue';
import { getStudentChanges, studentChangeKey } from '@/utils/Routine';
import EditSetsDialog from './EditSetsDialog.vue';
import WeightModeExercises from './WeightModeExercises.vue';
import ChangeExerciseDialog from '../ChangeExerciseDialog.vue';
import PainScaleDialog from '../PainScaleDialog.vue';
import RecordVideoDialog from '../RecordVideoDialog.vue';
import ExerciseHistoryDialog from '../../coach/routines/ExerciseHistoryDialog.vue';
import MobileSpacer from '../MobileSpacer.vue';

export default {
    props: {
        assignedRoutineId: {
            type: String,
            default: '',
        },
        readonly: {
            type: Boolean,
            default: false
        },
        showCompleteSets: {
            type: Boolean,
            default: false
        },
        hideStudentInfo: {
            type: Boolean,
            default: false,
        },
        noContainer: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        BlockExpansionHeader,
        SkeletonLoader,
        RestCard,
        ExerciseCard,
        RoutineFooter,
        NoteMessageDialog,
        TimerDialog,
        WatchVideoDialog,
        EditSetsDialog,
        WeightModeExercises,
        ChangeExerciseDialog,
        PainScaleDialog,
        RecordVideoDialog,
        ExerciseHistoryDialog,
        MobileSpacer
    },
    data() {
        return {
            loading: false,
            assignedRoutine: null,
            routine: null,
            carousel: 0,
            loadingComplete: false,
            video: null,
            img: null,
            exerciseEditing: null,
            commentary: null,
        }
    },
    computed: {
        ...mapState(['currentRoutine', 'equipment', 'currentCarousel']),
        routineImg(vm = this) {
            return vm.currentRoutine?.img || vm.imgPlaceholder
        },
        objective(vm = this) {
            return vm.currentRoutine?.description?.objective || vm.$t('common.not_available_short')
        },
        equipments(vm = this) {
            return vm.currentRoutine?.description?.equipments || []
        },
        blocks(vm = this) {
            return blockUniqueTime(vm.currentRoutine?.blocks || [])
        },
        studentChanges(vm = this) {
            if (!vm.readonly) return null
            return getStudentChanges(vm.currentRoutine)
        },
        routineDuration(vm = this) {
            if (!vm.currentRoutine) return vm.$t('common.not_available_short')

            let minutes = 0

            vm.currentRoutine?.blocks?.forEach(block => {
                block?.exercises?.forEach(exercise => {
                    minutes += exercise?.config?.sets?.length || block.sets || 0
                })
            })

            let minutesAdded = Number(minutes * 1.3).toFixed()


            if (Number(minutes) === Number(minutesAdded)) return minutes
            return `${minutes} - ${minutesAdded}`
        },
        percentReady(vm = this) {
            if (!vm.currentRoutine) return 0
            let qtyExercises = 0
            let completed = 0

            vm.currentRoutine?.blocks?.forEach(block => {
                let exercises = (block?.exercises || []).filter(ex => ex?.type !== 'rest').length
                if (block.completed) {
                    qtyExercises += exercises
                    completed += exercises
                } else qtyExercises += exercises
            })

            return parseInt((completed * 100) / qtyExercises) || 0
        },
        exercisesQuantity(vm = this) {
            let exercises = 0
            vm.currentRoutine?.blocks?.forEach(block => {
                exercises += block?.exercises?.filter(exercise => exercise?.type === 'exercise')?.length || 0
            })
            return exercises
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        },
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        isPlaylistWeightMode(vm = this) {
            return vm.account?.playlist?.mode === 'only_weight'
        },
        weightExercises(vm = this) {
            const exercises = (vm.currentRoutine?.blocks || [])
                .flatMap((block, blockIndex) =>
                    (block.exercises || [])
                        .filter(exercise => exercise.weightMode === true)
                        .map(exercise => ({
                            ...exercise,
                            block: blockIndex + 1
                        }))
                );

            return exercises;
        },
        isCompetence(vm = this) {
            return vm.currentRoutine?.type === 'competence'
        },
        competitionInfo() {
            const info = [
                {
                    label: this.$tc('common.minutes', 2),
                    value: this.currentRoutine?.minutes
                },
                {
                    label: this.$t('common.date'),
                    value: this.currentRoutine?.rawDate?.full
                        ? this.$moment(this.currentRoutine.rawDate.full).format('DD-MM-YYYY')
                        : null
                },
                {
                    label: this.$t('common.duration'),
                    value: this.currentRoutine?.advance?.postInfo?.duration || null
                },
                {
                    label: this.$t('common.effort'),
                    value: this.currentRoutine?.advance?.postInfo?.effort || null
                },
                {
                    label: this.$t('common.position'),
                    value: this.currentRoutine?.advance?.postInfo?.position || null
                },
                {
                    label: this.$t('common.team'),
                    value: this.currentRoutine?.advance?.postInfo?.team || null
                },
                {
                    label: this.$t('common.comment'),
                    value: this.currentRoutine?.advance?.postInfo?.comment || null
                }
            ]

            return info.filter(item => item.value)
        },
    },
    async mounted() {
        const vm = this
        vm.loading = true
        if (vm.currentCarousel) vm.carousel = vm.currentCarousel
        if (vm.equipment.length === 0) await vm.bindEquipment()
        await vm.setAssignedRoutine()
        await vm.loadShowNote()
        vm.loading = false
    },
    methods: {
        ...mapMutations(['setCurrentRoutine', 'setTitle']),
        ...mapActions(['bindEquipment']),
        errorContext(vm = this) {
            return `Doc: assignedRoutines/${vm.assignedRoutineId || 'n/d'} \n`
                + `Dueno del doc: ${vm.currentRoutine?.studentId || 'n/d'} \n`
                + `Coach del doc: ${vm.currentRoutine?.coachId || 'n/d'} \n`
                + `Sesion: ${vm.currentUser?.uid || 'n/d'} \n`
        },
        async setAssignedRoutine() {
            const vm = this
            let assignedRoutineRef = await db.collection('assignedRoutines').doc(vm.assignedRoutineId).get()
            vm.assignedRoutine = assignedRoutineRef.data()
            if (!vm.assignedRoutine) return

            if (vm.assignedRoutine.routineId) {
                let routineRef = await db.collection('routines').doc(vm.assignedRoutine.routineId).get()
                vm.routine = routineRef.data()
            }

            let routineName = vm.assignedRoutine?.name || vm.routine?.name || vm.assignedRoutine?.versus || vm.$t('common.not_available_short')
            vm.setTitle(routineName)

            let currentRoutine = {
                assignedRoutineId: vm.assignedRoutineId,
                name: routineName,
                img: vm.assignedRoutine?.img || vm.routine?.img || null,
                description: vm.routine?.description || null,
                ...vm.assignedRoutine
            }

            currentRoutine.blocks = await getMergedBlocks(vm.assignedRoutine.blocks)
            vm.setCurrentRoutine(currentRoutine)
        },
        equipImg(equip) {
            let vm = this
            let findEquip = vm.equipment.find(el => el.name === equip)
            return findEquip && findEquip.src || vm.imgPlaceholder
        },
        showExerciseDialog(exercise) {
            let vm = this
            vm.$refs.exerciseDialog.show()
            vm.$refs.exerciseDialog.setExercise(exercise)
        },
        async setBlockComplete(completed, block) {
            const vm = this
            if (!vm.assignedRoutine?.blocks?.[block]) return
            vm.loadingComplete = true

            try {
                const routineRef = await db.collection('assignedRoutines').doc(vm.assignedRoutineId).get()
                if (!routineRef.exists) return vm.notifyAssignedRoutineGone(vm.assignedRoutineId)

                const blocks = routineRef.data()?.blocks || []
                if (!blocks[block]) return

                blocks[block].completed = completed
                ;(blocks[block].exercises || []).forEach(exercise => {
                    if (exercise.type !== 'exercise') return
                    exercise.completed = completed
                })

                if (!await vm.saveAssignedRoutine(vm.assignedRoutineId, { blocks })) return

                vm.assignedRoutine = { ...vm.assignedRoutine, blocks }
                vm.applyBlockCompleted(block, completed)
            } catch (e) {
                vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
            } finally {
                vm.loadingComplete = false
            }
        },
        applyBlockCompleted(index, completed) {
            const vm = this
            const current = vm.currentRoutine
            if (!current?.blocks?.[index]) return

            const blocks = current.blocks.map((item, i) => {
                if (i !== index) return item
                return {
                    ...item,
                    completed,
                    exercises: item.exercises.map(exercise => {
                        if (exercise.type !== 'exercise') return exercise
                        return { ...exercise, completed }
                    }),
                }
            })

            vm.setCurrentRoutine({ ...current, blocks })
        },
        async loadShowNote() {
            const vm = this;
            if (!vm.assignedRoutine || !vm.studentId) return
            if (!vm.assignedRoutine.rawDate?.full) return

            let noteRef = await db.collection('routineNotes')
                .where('studentId', '==', vm.studentId)
                .where('rawDate.full', "==", vm.assignedRoutine.rawDate.full)
                .where('routineNumber', "==", vm.assignedRoutine.routineNumber || 1)
                .get()

            if (noteRef.docs[0]?.data().showPopup) {
                if (!vm.$refs.note) return
                let message = noteRef.docs[0].data().message
                db.collection('routineNotes').doc(noteRef.docs[0].id).update({ showPopup: false })
                vm.$refs.note.show(message)
            }
        },
        showTimerDialog(i) {
            const vm = this;
            vm.$refs.blockHeader[i].showTimerDialog()
        },
        changesFor(blockIndex, exercise) {
            if (!this.studentChanges) return null
            const byId = studentChangeKey(blockIndex + 1, exercise)
            return (byId && this.studentChanges.get(byId)) || this.studentChanges.get(`${blockIndex + 1}|${exercise?.name}`) || null
        },
        onWatchStudentVideo(change) {
            const vm = this
            if (!change?.url) return
            vm.commentary = null
            vm.img = require(`@/assets/img/brand_mark.png`)
            vm.video = change.url
            vm.$nextTick(() => {
                if (vm.$refs.watchVideo) vm.$refs.watchVideo.show(false)
            })
        },
        onClickExerciseImage(exercise) {
            const vm = this;
            vm.commentary = exercise?.commentary || null
            const isYoutubeVideo = exercise?.youtubeUrl ? true : false
            let img = exercise.img || (exercise?.video?.thumb) || null
            if (isYoutubeVideo) img = vm.getYoutubeImgUrl(exercise.youtubeUrl)
            vm.img = img || require(`@/assets/img/brand_mark.png`)
            vm.video = isYoutubeVideo ? exercise.youtubeUrl : exercise?.video?.url || null
            vm.$nextTick(() => {
                if (vm.$refs.watchVideo) vm.$refs.watchVideo.show(isYoutubeVideo)
            })
        },
        onClickExerciseConfig(exercise, block, blockIndex, exerciseIndex) {
            const vm = this;
            vm.commentary = exercise?.commentary || null
            vm.$refs.editSets.show({
                ...exercise,
                assignedRoutineId: vm.assignedRoutineId,
                exerciseId: exercise.id,
                config: exercise.config,
                block: blockIndex + 1,
                name: exercise.name,
                blockData: block,
                routine: vm.currentRoutine,
                category: exercise.categories?.[0] || null,
                exerciseIndex: exerciseIndex,
            })
        },
        exerciseAt(blockIndex, exerciseIndex) {
            return this.currentRoutine?.blocks?.[blockIndex]?.exercises?.[exerciseIndex] || null
        },
        onUpdatedSet({ block, exerciseIndex, config }) {
            const exercise = this.exerciseAt(block - 1, exerciseIndex)
            if (exercise) exercise.config = config
        },
        onSentCommentary({ comment, blockIndex, exerciseIndex }) {
            const exercise = this.exerciseAt(blockIndex, exerciseIndex)
            if (exercise) exercise.commentaryFromStudent = comment
        },
        async onCompleteWeightRoutine() {
            await this.setAssignedRoutine()
            this.$emit('complete')
        },
        // MENU ACTIONS
        handleTimer() {
            const vm = this;
            vm.$refs.timer.show()
        },
        handleExercise(exercise, blockIndex, exerciseIndex) {
            const vm = this
            vm.exerciseEditing = {
                ...exercise,
                blockIndex: blockIndex + 1,
                exerciseIndex: exerciseIndex,
            }
            vm.$refs.changeExercise.show()
        },
        async onChangeExerciseSuccess({ oldExercise, newExercise }) {
            const vm = this;
            if (!vm.exerciseEditing) return

            let currentRoutine = { ...vm.currentRoutine }
            const blockIndex = vm.exerciseEditing.blockIndex - 1
            const exerciseIndex = vm.exerciseEditing.exerciseIndex
            currentRoutine.blocks[blockIndex].exercises.splice(exerciseIndex, 1, newExercise)

            const saved = await vm.saveAssignedRoutine(vm.assignedRoutineId, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'exercise',
                    block: vm.exerciseEditing.blockIndex,
                    exerciseId: oldExercise.objectID || oldExercise.id || null,
                    oldExercise: oldExercise.name,
                    newExercise: newExercise.name,
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.exercise_changed'))
            vm.exerciseEditing = null
        },
        handleCamera(exercise, blockIndex, exerciseIndex) {
            const vm = this
            vm.exerciseEditing = {
                ...exercise,
                block: blockIndex + 1,
                blockIndex: blockIndex + 1,
                exerciseIndex: exerciseIndex,
            }
            vm.$refs.recordVideo.show()
        },
        async handleSkip(exercise, blockIndex) {
            const vm = this;
            const saved = await vm.saveAssignedRoutine(vm.assignedRoutineId, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'skip',
                    block: blockIndex + 1,
                    exercise: exercise.name,
                    exerciseId: exercise.objectID || exercise.id || null,
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.exercise_skipped'))
        },
        handleHistory(exercise) {
            const vm = this;
            vm.$refs.history.show(exercise)
        },
        handlePainScale(exercise, blockIndex) {
            const vm = this;
            vm.exerciseEditing = {
                ...exercise,
                blockIndex: blockIndex + 1,
            }
            vm.$refs.painScale.show()
        },
        async onPainScale(scale) {
            const vm = this;
            if (!vm.exerciseEditing) return
            const saved = await vm.saveAssignedRoutine(vm.assignedRoutineId, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'painScale',
                    block: vm.exerciseEditing.blockIndex,
                    exercise: vm.exerciseEditing.name,
                    exerciseId: vm.exerciseEditing.objectID || vm.exerciseEditing.id || null,
                    scale: scale
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.pain_scale_saved'))
            vm.exerciseEditing = null
        },
    },
    watch: {
        percentReady(val) {
            if (val === 100)
                setTimeout(() => {
                    this.$emit('complete')
                }, 500)
        }
    }
}
</script>

<style scoped>
.img-routine {
    background: var(--v-surface2-base);
    height: 220px;
    border-radius: 14px;
}

.img-routine-mobile {
    max-width: unset;
    margin: 0 -24px -12px;
    border-radius: 0;
}

/* ── Stats ── */
.stat-row {
    display: flex;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.stat-row-mobile {
    max-width: unset;
    margin: -12px -24px;
    border-radius: 0;
}

.stat-tile {
    flex: 1;
    padding: 16px 8px;
    text-align: center;
    border-right: 1px solid var(--v-outline-base);
}

.stat-tile:last-child {
    border-right: none;
}

.stat-lbl {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--v-txtMuted-base);
    margin-bottom: 4px;
}

.stat-val {
    font-size: 20px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

/* ── Competence info ── */
.comp-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 14px 16px;
}

.comp-card-mobile {
    max-width: unset;
    margin: -12px -24px 0;
    border-radius: 0;
}

.comp-title {
    text-align: center;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-accent-base);
    margin-bottom: 10px;
}

.theme--dark .comp-title {
    color: var(--v-primary-base);
}

.comp-item {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.comp-item:last-child {
    border-bottom: none;
}

.comp-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.comp-value {
    font-size: 13px;
    font-weight: 600;
    text-align: right;
    color: var(--v-txtPrimary-base);
}

/* ── Blocks ── */
.blocks-panels {
    background: transparent;
}

.blocks-panels ::v-deep>.v-expansion-panel {
    background: transparent;
    margin-bottom: 6px;
    border-radius: 12px !important;
    overflow: hidden;
}

.blocks-panels ::v-deep>.v-expansion-panel::before {
    box-shadow: none !important;
}

.blocks-panels ::v-deep>.v-expansion-panel::after {
    display: none;
}

.blocks-panels ::v-deep .v-expansion-panel-content {
    background: var(--v-surface2-base);
}

.blocks-panels ::v-deep .v-expansion-panel-content__wrap {
    padding: 6px 10px 4px;
}

.ex-wrap {
    margin-top: 6px;
}

.equipments {
    font-size: 14px;
    color: var(--v-txtSecondary-base);
}
</style>